import { DatasetQueryConverterConfig } from '@warebee/shared/export-converter';
import _ from 'lodash';
import { nanoid } from 'nanoid';
import { useTranslation } from 'react-i18next';
import { useRecoilCallback } from 'recoil';
import { AsyncLoadStatus } from '../../common/types';
import useExportDatasetQuery from '../../hooks/useExportDatasetQuery';
import useExportFilename from '../../hooks/useExportFilename';
import { errorAppender } from '../../store/error.state';
import {
    actualityAggregatedStatsByGroups,
    actualityAggregatedStatsGroupBy,
} from '../store/actuality.kpi.state';
import {
    actualityKpiMetricsTableData,
    actualityKpiMetricsTableDataLoadStatus,
    actualityKpiMetricsTableDataState,
    actualityKpiMetricsTableTotalCount,
} from '../store/actuality.kpiMetrics.state';
import { KpiGroupRow } from '../store/actuality.types';
import { getActualityAggregationMetricDescriptorsMap } from '../store/feedMetrics';

export type LoadActualityKpiMetricsTableDataParams = {
  searchValues?: Record<string, string>;
  isAppend?: boolean;
};

export type ExportActualityKpiMetricsTableDataParams =
  LoadActualityKpiMetricsTableDataParams & {
    converterConfig: DatasetQueryConverterConfig<any>;
  };

function useLoadActualityKpiMetricsTableData() {
  const { t } = useTranslation('errors');
  const exportFilename = useExportFilename({ title: t`kpi-metrics` });
  const exportDataset = useExportDatasetQuery();
  const errorTitle = t`Cannot load KPI Metrics`;

  const resetCallback = useRecoilCallback(({ reset }) => async () => {
    reset(actualityKpiMetricsTableDataLoadStatus);
    reset(actualityKpiMetricsTableData);
    reset(actualityKpiMetricsTableDataState);
    reset(actualityKpiMetricsTableTotalCount);
  });

  const initLoading = useRecoilCallback(({ set }) => () => {
    set(actualityKpiMetricsTableDataLoadStatus, AsyncLoadStatus.Loading);
  });

  const loadDataCallback = useRecoilCallback(
    ({ snapshot, set }) =>
      async (params: LoadActualityKpiMetricsTableDataParams) => {
        try {
          initLoading();

          // Get the current groupBy field
          const groupBy = await snapshot.getPromise(
            actualityAggregatedStatsGroupBy,
          );

          // Get grouped data
          const groupedData = await snapshot.getPromise(
            actualityAggregatedStatsByGroups([groupBy]),
          );

          // Get metric descriptors (available metrics that have data)
          const metricDescriptors = getActualityAggregationMetricDescriptorsMap(
            t as any,
            {
              formatInteger: (v: number, unit?: string) => ({
                value: v.toLocaleString(),
                fullString: `${v.toLocaleString()}${unit ? ` ${unit}` : ''}`,
                unit: unit || '',
                raw: v,
                unitCode: 'NUMBER',
              }),
              formatCurrency: (v: number) => ({
                value: `$${v.toFixed(2)}`,
                fullString: `$${v.toFixed(2)}`,
                unit: '$',
                raw: v,
                unitCode: 'USD',
              }),
              formatTimeSpan: (v: number) => ({
                value: `${Math.round(v / 1000)}s`,
                fullString: `${Math.round(v / 1000)} seconds`,
                unit: 's',
                raw: v,
                unitCode: 'MS',
              }),
              formatDistance: (v: number) => ({
                value: `${v.toFixed(1)}m`,
                fullString: `${v.toFixed(1)} meters`,
                unit: 'm',
                raw: v,
                unitCode: 'M',
              }),
              formatPercentage: (v: number) => ({
                value: `${v.toFixed(1)}%`,
                fullString: `${v.toFixed(1)}%`,
                unit: '%',
                raw: v,
                unitCode: 'PERCENT',
              }),
            } as any,
          );

          // Transform grouped data into table rows
          const groupRows: KpiGroupRow[] = _.map(
            groupedData,
            (groupRow, index) => {
              const groupValue = groupRow[groupBy] as string;
              const baseRow: KpiGroupRow = {
                groupId: `${groupBy}-${groupValue}-${index}`,
                groupValue: groupValue,
                groupLabel: `${groupBy}: ${groupValue}`,
              };

              // Add all metric values as columns
              Object.keys(metricDescriptors).forEach(metricId => {
                baseRow[metricId] =
                  (groupRow[metricId as keyof typeof groupRow] as number) || 0;
              });

              return baseRow;
            },
          );

          // Apply search filters if provided
          let filteredData = groupRows;
          if (
            params.searchValues &&
            Object.keys(params.searchValues).length > 0
          ) {
            filteredData = _.filter(groupRows, row => {
              return _.some(params.searchValues, (searchTerm, field) => {
                if (!searchTerm) return true;
                const fieldValue = _.get(row, field);
                return _.toString(fieldValue)
                  .toLowerCase()
                  .includes(searchTerm.toLowerCase());
              });
            });
          }

          set(actualityKpiMetricsTableData, filteredData);
          set(actualityKpiMetricsTableTotalCount, filteredData.length);
          set(actualityKpiMetricsTableDataLoadStatus, AsyncLoadStatus.Ok);
        } catch (error) {
          console.error('Error loading KPI metrics data:', error);
          set(errorAppender, {
            id: nanoid(),
            title: errorTitle,
            details: String(error),
          });
          set(actualityKpiMetricsTableDataLoadStatus, AsyncLoadStatus.Error);
        }
      },
  );

  //   const exportCSVCallback = useRecoilCallback(
  //     ({ snapshot }) =>
  //       async (params: ExportActualityKpiMetricsTableDataParams) => {
  //         const data = await snapshot.getPromise(actualityKpiMetricsTableData);

  //         return exportDataset({
  //           filename: exportFilename,
  //           converterConfig: params.converterConfig,
  //           query: async () => ({ rows: data }),
  //         });
  //       },
  //   );

  return [loadDataCallback, resetCallback] as const;
//   return [loadDataCallback, resetCallback, exportCSVCallback] as const;
}

export default useLoadActualityKpiMetricsTableData;
